import React from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useMsal } from "@azure/msal-react";
import {
  getCoursesGroupedByCategoryAndModule,
  isFoundationUnlocked,
} from "utils/foundationUnlock";
import { dashboardAssets } from "../../imagesData/ImagesData";
import useFetchImageUrls from "../../ConstantUseFetchImageUrls";

const ALL_HUBS = [
  { key: "foundation", label: "Core Hub", backendName: "Foundation" },
  {
    key: "genai",
    label: "GenAI Agentic Hub",
    backendName: "GenAI and Agentic AI Hub",
  },
  {
    key: "sales-solutioning",
    label: "Sales & Solutioning Hub",
    backendName: "Sales & Solutioning Hub",
  },
  {
    key: "delivery-modernization",
    label: "Delivery Modernisation Hub",
    backendName: "Delivery Modernisation Hub",
  },
  {
    key: "future-experience",
    label: "Future of Experience Hub",
    backendName: "Future of Experience Hub",
  },
  { key: "product", label: "Product Hub", backendName: "Product Hub" },
  {
    key: "digital-customer",
    label: "Digital Customer.Next Hub",
    backendName: "Digital Customer.Next Hub",
  },
];

const BADGE_HIERARCHY = ["bronze", "silver", "gold", "platinum", "diamond"];

const getHubBadgeName = (hubData) => {
  if (!hubData?.courses?.length) return null;

  let highestBadgeIndex = -1;

  hubData.courses.forEach((course) => {
    if (typeof course.badge === "string") {
      const badge = course.badge.trim().toLowerCase();
      const badgeIndex = BADGE_HIERARCHY.indexOf(badge);

      if (badgeIndex > highestBadgeIndex) {
        highestBadgeIndex = badgeIndex;
      }
    }
  });

  if (highestBadgeIndex === -1) return null;

  const highestBadge = BADGE_HIERARCHY[highestBadgeIndex];
  return highestBadge.charAt(0).toUpperCase() + highestBadge.slice(1);
};

function DashboardMilestoneSection() {
  const history = useHistory();

  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const userLevelDes = activeAccount?.idTokenClaims?.jobFamilyDescription;

  const userData = useSelector((state) => state.userDetails.userData?.[0]);

  const isEmpty = (value) =>
    value === null || value === undefined || value === "" || value === "NULL";
  const userLevel = isEmpty(userLevelDes) ? userData?.level : userLevelDes;

  const { coursesByHub } = useSelector((state) => state.courseDetails);

  const hubMap = {};
  (coursesByHub || []).forEach((hub) => {
    if (hub?.hubName) {
      hubMap[hub.hubName.trim()] = hub;
    }
  });

  const foundationHub = hubMap["Foundation"];

  const isFoundationComplete =
    foundationHub && userData
      ? isFoundationUnlocked({
          coursesByHub,
          userData,
          userLevel,
        })
      : false;

  const computeHubProgress = (hub) => {
    if (!hub?.courses?.length || !userData) {
      return { progress: 0, completed: 0, total: 0 };
    }

    const groupedCourses = getCoursesGroupedByCategoryAndModule({
      coursesByHub: [hub],
      userData,
      userLevel,
      hubName: hub.hubName,
    });

    let totalMandatory = 0;
    let completedMandatory = 0;

    Object.values(groupedCourses || {}).forEach((category) => {
      Object.values(category || {}).forEach((moduleObj) => {
        const mandatoryCourses = moduleObj?.Mandatory || [];

        totalMandatory += mandatoryCourses.length;

        completedMandatory += mandatoryCourses.filter(
          (c) => c?.completionDate,
        ).length;
      });
    });

    const progress =
      totalMandatory > 0
        ? Math.round((completedMandatory / totalMandatory) * 100)
        : 0;

    return {
      progress,
      completed: completedMandatory,
      total: totalMandatory,
    };
  };

  const renderContinueButton = (isLocked, isComplete) => {
    if (isLocked || isComplete) return null;

    return (
      <a
        role="button"
        onClick={() => history.push("/learningHubs")}
        style={{ cursor: "pointer", color: "white" }}
      >
        <ArrowForwardIcon /> Continue
      </a>
    );
  };

  const query = `params=${encodeURIComponent(JSON.stringify(dashboardAssets))}`;
  const { imageUrls, error } = useFetchImageUrls(query);

  const urls = imageUrls?.imageUrl || {};

  if (error) return <div>Error loading assets.</div>;
  if (!imageUrls || Object.keys(imageUrls).length === 0) return null;

  return (
    <>
      {ALL_HUBS.map((hub) => {
        const hubData = hubMap[hub.backendName];

        let isLocked = false;

        if (hub.backendName !== "Foundation") {
          isLocked = !isFoundationComplete;
        }

        let progress = 0;
        let completed = 0;
        let total = 0;

        if (!isLocked && hubData) {
          const result = computeHubProgress(hubData);
          progress = result.progress;
          completed = result.completed;
          total = result.total;
        }

        const isComplete = progress === 100;
        const badgeName = getHubBadgeName(hubData);

        return (
          <div
            key={hub.key}
            className={`dashboard-con-lear-wrap ${
              isLocked ? "wrap-disabled" : ""
            }`}
          >
            <div className="dashboard-con-lear-row row1" data-aos="fade-left">
              <img
                // src={
                //   process.env.PUBLIC_URL +
                //   (isComplete
                //     ? "/Assets/Dashboard/milestone-complete-icon.png"
                //     : isLocked
                //       ? "/Assets/Dashboard/milestone-lock-icon.png"
                //       : "/Assets/Dashboard/continue-learning-icon.png")
                // }
                src={
                  isComplete
                    ? urls["milestoneCompleteicon"]
                    : isLocked
                      ? urls["milestoneLockicon"]
                      : urls["continueLearningicon"]
                }
                alt="hub-status"
                className="milestone-icon"
              />
            </div>

            <div className="dashboard-con-lear-row row2" data-aos="zoom-in">
              <h4>{hub.label}</h4>

              {isLocked ? (
                <>
                  <p>Unlock by completing Core Hub</p>

                  <div className="progress-bar-box">
                    <div className="progress-container">
                      <div className="progress-header">
                        <span className="progress-value">0% complete</span>
                        <span className="progress-value">
                          0/0 courses complete
                        </span>
                      </div>

                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{ width: "0%" }}
                        />
                      </div>
                    </div>

                    <span className="progress-label">
                      Complete and earn a <b>badge</b>
                    </span>
                  </div>
                </>
              ) : (
                <>
                  {/* <p className="hub-desc">
                    {hubData?.courses?.[0]?.courseDesc || ""}
                  </p> */}

                  <div className="progress-bar-box">
                    <div className="progress-container">
                      <div className="progress-header">
                        <span className="progress-value">
                          {progress}% complete
                        </span>
                        <span className="progress-value">
                          {completed}/{total} courses complete
                        </span>
                      </div>

                      <div className="progress-track">
                        <div
                          className={`progress-fill ${
                            isComplete ? "progress-complete" : ""
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    <span className="progress-label">
                      This progress is calculated based only on your mandatory
                      courses.
                    </span>
                  </div>
                </>
              )}
            </div>

            {!isComplete && (
              <div
                className="dashboard-con-lear-row row3"
                data-aos="fade-right"
              >
                <p className="ask-iva-link">
                  {renderContinueButton(isLocked, isComplete)}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

export default DashboardMilestoneSection;
